import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import Holidays from 'date-holidays';
import { ExplorerService } from 'ngx-explorer';
import {
  BehaviorSubject,
  catchError,
  delay,
  lastValueFrom,
  Observable,
  Subject,
  tap,
  throwError,
} from 'rxjs';
import { PdfViewerComponent } from '../components/pdf-viewer/pdf-viewer.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';
import { ActivityService } from './activity.service';
import { DealsService } from './deals.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PopupAndNotifService } from './popup-and-notif.service';
import { legacyConfirmIconTone } from './dialog-shell-rules';
import { hasBillingInformation as hasOpportunityBillingInformation } from './opportunity-workflow-rules';

export const listeProposalBU = ['agency_dette_privee'];

/** User catalog helpers used by adoption monitoring and closed deals screens. */
export interface ClosedDealUserCatalogSupport {
  get_all_users_closed_deal(): Observable<any>;
  getFullName2(slug: string): string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperService implements ClosedDealUserCatalogSupport {
  public isLoadingSubject$: Subject<boolean> = new BehaviorSubject(false);
  public isLoading$ = this.isLoadingSubject$.asObservable().pipe(delay(0));

  // Spinner avec message
  private spinnerLoadingSubject = new BehaviorSubject<boolean>(false);
  private spinnerMessageSubject = new BehaviorSubject<string>('');
  public spinnerLoading$ = this.spinnerLoadingSubject.asObservable();
  public spinnerMessage$ = this.spinnerMessageSubject.asObservable();

  hd = new Holidays('FR');
  appLoading: boolean = true;

  public allUsers: any[] = [];
  public allCorporates: any[] = [];

  holidayCache: { [year: number]: any[] } = {};
  serverUrl = environment.serverUrl;

  /**
   * Identity of the signed-in user, read from the AuthService signal on every access.
   *
   * These used to be fields filled once in the constructor. HelperService is injected by
   * AppComponent, so it is built at bootstrap — on the login page, before anyone is signed in.
   * The snapshot then stayed empty for the whole SPA session: `hasCrmManagerAccess()` and
   * `isSuperAdmin()` answered `false` right after a login, and only a page reload (which rebuilds
   * the service with sessionStorage already filled) made them true. That is what hid the
   * "Bus dev in charge" filter of /crm/crm-all-appointments from a superadmin until F5.
   *
   * Reading the signal keeps them live, and makes the manual `helperService.group = …` patches
   * the login screens used to apply unnecessary.
   */
  get currentUser(): any {
    return this.authService.currentUser();
  }

  get username(): string {
    return this.currentUser?.username;
  }

  get first_name(): string {
    return this.currentUser?.first_name;
  }

  get last_name(): string {
    return this.currentUser?.last_name;
  }

  get title(): string {
    return this.currentUser?.title;
  }

  get token(): string {
    return this.currentUser?.token;
  }

  get group(): any {
    return this.currentUser?.group;
  }

  get groupdesc(): string {
    return this.currentUser?.description_group;
  }

  get userid(): string {
    return this.currentUser?.user_id;
  }

  get user_email(): string {
    // The login payload names it `user_email`; `email` is the shape some older callers stored.
    return this.currentUser?.user_email ?? this.currentUser?.email;
  }

  constructor(
    private http: HttpClient,
    private activityservice: ActivityService,
    private dealservice: DealsService,
    public authService: AuthService,
    private dialog: MatDialog,
    private toast: ToastrService,
    private explorerService: ExplorerService,
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    private ngZone: NgZone,
    private popupAndNotifService: PopupAndNotifService
  ) {
    // No identity snapshot here on purpose: this service is built at bootstrap, before login.
    // The getters above read AuthService on every access instead.
  }

  getMeetingDuration(duration: number): string {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return hours > 0 ? `${hours}h${minutes}min` : `${minutes}min`;
  }

  isSuperAdmin(): boolean {
    if (!this.currentUser) return false;
    return (
      this.currentUser &&
      this.currentUser.group &&
      this.currentUser.group.includes('superadmin')
    );
  }

  hasCrmManagerAccess(): boolean {
    if (!this.currentUser) return false;
    return (
      this.currentUser &&
      this.currentUser.group &&
      (this.currentUser.group.includes('crm_manager') ||
        this.currentUser.group.includes('superadmin'))
    );
  }

  private findUserBySlugOrUsername(entry: string) {
    const trimmed = typeof entry === 'string' ? entry.trim() : entry;
    if (!trimmed) {
      return undefined;
    }
    return this.allUsers.find(
      user => user.slug === trimmed || user.username === trimmed
    );
  }

  getFullName(slug: string) {
    if (slug && slug.includes(';')) {
      const slugs = slug.split(';').map(s => s.trim()).filter(Boolean);
      const names = slugs
        .map(s => {
          const user = this.findUserBySlugOrUsername(s);
          return user ? `${user.last_name} ${user.first_name}`.trim() : '';
        })
        .filter(Boolean);
      return names.length ? names.join('; ') : 'N/A';
    }

    const user = this.findUserBySlugOrUsername(slug);
    return user ? `${user.last_name} ${user.first_name}`.trim() : 'N/A';
  }


  public getFullName2(slug: string): string {
    
    if (slug == null || slug == undefined || slug == '') {
      return 'N/A';
    }
    if (!slug || (typeof slug === 'string' && !slug.trim())) {
      return '';
    }

    const resolveEntry = (entry: string): string => {
      const user = this.findUserBySlugOrUsername(entry);
      if (user) {
        return `${user.last_name} ${user.first_name}`.trim();
      }
      return 'N/A';
    };

    if (slug.includes(';')) {
      return slug
        .split(';')
        .map(s => s.trim())
        .filter(Boolean)
        .map(resolveEntry)
        .join('; ');
    }

    return resolveEntry(slug.trim());
  }

  getFullNameSociete(slug_societe: string) {
    if (!slug_societe) {
      return 'N/A';
    }

    // Si les sociétés ne sont pas encore chargées, retourner le slug temporairement
    if (!this.allCorporates || this.allCorporates.length === 0) {
      return slug_societe;
    }

    if (slug_societe && slug_societe.includes(';')) {
      const slugs = slug_societe.split(';');
      let fullName = '';
      slugs.forEach(slug => {
        const trimmedSlug = slug.trim();
        if (trimmedSlug) {
          const corporate = this.allCorporates.find(
            corp => corp.slug === trimmedSlug
          );
          if (corporate) {
            fullName += corporate.name + '; ';
          } else {
            // Si la société n'est pas trouvée, afficher le slug
            fullName += trimmedSlug + '; ';
          }
        }
      });
      return fullName.trim();
    } else {
      const corporate = this.allCorporates.find(
        corp => corp.slug === slug_societe
      );
      return corporate ? corporate.name : slug_societe;
    }
  }

  public hasBillingInformation(opportunity: any): boolean {
    return hasOpportunityBillingInformation(opportunity);
  }

  public showLoading() {
    //this.loadingService.showLoad();
    this.isLoadingSubject$.next(true);
  }

  public hideLoading() {
    //this.loadingService.hideLoad();
    this.isLoadingSubject$.next(false);
  }

  public goTo(url: string, param: any = null) {
    this.router.navigateByUrl(url);
  }

  public expandNode(nodeId: number) {
    this.explorerService.expand(nodeId);
  }

  public collapseNode(nodeId: number) {
    //this.explorerService.collapse(nodeId);
  }

  reloadCurrentRoute() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  /**
   * @author Neji Kalboussi
   * Download a base64 file encoded
   * @param data object {fileName:string, response: string}
   */
  public downloadFile(data: any) {
    const fileName = data.fileName;
    const src = data.response;
    const link = document.createElement('a');
    link.href = src;
    link.download = fileName;
    link.click();
    link.remove();
  }

  /**
   * Download one Commitments Committee attachment.
   *
   * CDE files live in the internal GED, so the API streams them instead of inlining a
   * base64 payload in every get_cde_form response. `url` is the relative endpoint the
   * backend puts on each entry, and it also serves the legacy rows still on disk.
   */
  public downloadCdeFile(file: { url: string; fileOriginalName?: string; fileName?: string }) {
    const name = file.fileOriginalName || file.fileName || 'document';
    this.http
      .get(`${this.serverUrl}${file.url}`, { responseType: 'blob' })
      .subscribe({
        next: (blob: Blob) => this.downloadBlob(blob, name),
        error: () => this.openSnackBar('The document could not be downloaded.'),
      });
  }

  /**
   * Save a blob streamed by the API (files stored in the internal GED are never inlined
   * as base64 in JSON responses).
   */
  public downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  public openPDF(data) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const newUrl = URL.createObjectURL(blob);
    window.open(newUrl, '_blank');
  }

  async showPDF(
    item: any,
    download: boolean = false,
    title: string = ''
  ): Promise<void> {
    const dialogRef = this.dialog.open(PdfViewerComponent, {
      width: '800px',
      height: '800px',
      //maxHeight: '90%',
      maxWidth: '800px',
      data: {
        pdfView: item.response,
        fileName: item.fileName,
        download: download,
        title: title,
      },
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    return result;
  }

  public showAlert(title: string, message: string, icon: string = 'warning') {
    this.popupAndNotifService.showNotification(message, title, icon);
    return true;
  }

  /**
   * Legacy entry point, kept for its callers: delegates to PopupAndNotifService.confirm(), the
   * single place popups are opened. New code calls popupAndNotifService.confirm() directly.
   * Resolves true only when the user confirmed.
   */
  public showConfirmation(
    title: string,
    message: string,
    icon: string = 'warning'
  ): Promise<boolean> {
    return this.popupAndNotifService.confirm(title, message, { tone: legacyConfirmIconTone(icon) });
  }

  public isValidIBANNumber(input: string): boolean {
    const CODE_LENGTHS: { [key: string]: number } = {
      AD: 24,
      AE: 23,
      AT: 20,
      AZ: 28,
      BA: 20,
      BE: 16,
      BG: 22,
      BH: 22,
      BR: 29,
      CH: 21,
      CR: 21,
      CY: 28,
      CZ: 24,
      DE: 22,
      DK: 18,
      DO: 28,
      EE: 20,
      ES: 24,
      FI: 18,
      FO: 18,
      FR: 27,
      GB: 22,
      GI: 23,
      GL: 18,
      GR: 27,
      GT: 28,
      HR: 21,
      HU: 28,
      IE: 22,
      IL: 23,
      IS: 26,
      IT: 27,
      JO: 30,
      KW: 30,
      KZ: 20,
      LB: 28,
      LI: 21,
      LT: 20,
      LU: 20,
      LV: 21,
      MC: 27,
      MD: 24,
      ME: 22,
      MK: 19,
      MR: 27,
      MT: 31,
      MU: 30,
      NL: 18,
      NO: 15,
      PK: 24,
      PL: 28,
      PS: 29,
      PT: 25,
      QA: 29,
      RO: 24,
      RS: 22,
      SA: 24,
      SE: 24,
      SI: 19,
      SK: 24,
      SM: 27,
      TN: 24,
      TR: 26,
      AL: 28,
      BY: 28,
      EG: 29,
      GE: 22,
      IQ: 23,
      LC: 32,
      SC: 31,
      ST: 25,
      SV: 28,
      TL: 23,
      UA: 29,
      VA: 22,
      VG: 24,
      XK: 20,
    };

    let iban = String(input)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, ''); // keep only alphanumeric characters
    let code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/); // match and capture (1) the country code, (2) the check digits, and (3) the rest

    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
      return false;
    }

    // rearrange country code and check digits, and convert chars to ints
    let digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, letter => {
      return String(letter.charCodeAt(0) - 55);
    });

    // final check
    return this.mod97(digits) === 1;
  }

  public mod97(string: string): number {
    let checksum = string.slice(0, 2);
    let fragment: string;

    for (let offset = 2; offset < string.length; offset += 7) {
      fragment = checksum + string.substring(offset, offset + 7);
      checksum = (parseInt(fragment, 10) % 97).toString();
    }

    return parseInt(checksum, 10);
  }

  public formatDate(d: Date) {
    return (
      d.getFullYear() +
      '-' +
      ('0' + (d.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + d.getDate()).slice(-2)
    );
  }

  public contains(tab1, tab2) {
    return tab1.find(item => tab2.includes(item)) !== undefined;
  }

  isHoliday(date: Date): boolean {
    const finaldate = new Date(date);
    if (finaldate) {
      const year = finaldate.getFullYear();
      if (!this.holidayCache[year]) {
        this.holidayCache[year] = this.hd.getHolidays(year);
      }
      const holidays = this.holidayCache[year];

      return holidays.some(holiday => {
        const holidayDate = new Date(holiday.date);
        return (
          holidayDate.getFullYear() === finaldate.getFullYear() &&
          holidayDate.getMonth() === finaldate.getMonth() &&
          holidayDate.getDate() === finaldate.getDate()
        );
      });
    }
    return false;
  }

  isWeekend(date: Date): boolean {
    const finaldate = new Date(date);
    const dayOfWeek = finaldate ? finaldate.getDay() : null; // 0 pour dimanche, 6 pour samedi
    return dayOfWeek === 0 || dayOfWeek === 6; // True si c'est un week-end
  }

  clearCache() {
    if ('serviceWorker' in navigator) {
      caches.keys().then(function (cacheNames) {
        cacheNames.forEach(function (cacheName) {
          caches.delete(cacheName);
        });
        window.location.reload();
      });
    }
  }

  tofixed(value: number, precision: number) {
    return parseFloat((Math.round(value * 100) / 100).toFixed(precision));
  }

  openSnackBar(
    message: string,
    action: string = 'Close',
    duration: number = 5000
  ) {
    this.snackbar.open(message, action, { duration: duration });
  }

  getKYCValidityDate(item: any) {
    const currentDate = item.kyc_opening_date
      ? new Date(item.kyc_opening_date)
      : new Date();
    const criticality_level = item.criticality_level;
    let validityDate: Date;
    if (criticality_level == 'Low') {
      validityDate = new Date(
        currentDate.setFullYear(currentDate.getFullYear() + 3)
      );
    } else if (criticality_level == 'Medium') {
      validityDate = new Date(
        currentDate.setFullYear(currentDate.getFullYear() + 2)
      );
    } else if (criticality_level == 'High') {
      validityDate = new Date(
        currentDate.setFullYear(currentDate.getFullYear() + 1)
      );
    } else {
      validityDate = currentDate;
    }

    return validityDate;
  }

  getDealSituation(opportunity: any): string {
    if (opportunity.draft && opportunity.draft == '1') {
      return 'Opportunity Draft';
    }

    const status = opportunity.slug_status;
    const t = status.split('_');
    let result = '';
    t.forEach(element => {
      result += element + ' ';
    });

    return result.charAt(0).toUpperCase() + result.slice(1);

    /*else if (opportunity.slug_status === 'pending_deals' && opportunity.is_deal == "1" && (opportunity.is_closed == null || opportunity.is_closed == 'false')) {
      return 'Pending Deals(Awiting for billing informations)';
    }

    else if (opportunity.slug_status === 'project_won') {
      return 'Project Won';
    }
    else if (opportunity.slug_status === 'project_lost') {
      return 'Project Lost';
    }

    else if (opportunity.status === 'awaiting_assignment') {
      return 'Awaiting to exec assignment';
    }

    else if (opportunity.slug_status === 'awaiting_closing' && opportunity.state == 'awaiting_assignment' && (opportunity.is_deal == "0" || opportunity.is_deal == null)) {
      return 'Awaiting to exec assignment';
    }

    else if (opportunity.slug_status != 'pending_deals' && opportunity.is_deal == "1" && opportunity.is_closed == 'true' && opportunity.state === 'assigned') {
      return 'Closed Deal';
    }

    else if (opportunity.slug_status === 'in_process') {
      if (opportunity.is_deal == "1") {
        if (opportunity.exec_in_charge && opportunity.exec_in_charge != null && opportunity.exec_in_charge != '') {
          return 'Live Deal';
        }
        return 'Awaiting to exec assignment';
      }
      return 'In Progress';
    }

    else if (opportunity.slug_status === 'awaiting_additional_information') {
      return 'Awaiting additional information';
    }


    return 'Awaiting to Launch';*/
  }

  getCurrentUser() {
    //const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const currentUser = this.authService.currentUser;
    if (currentUser) {
      return currentUser;
    }
    return null;
  }

  convertSharePointToFileLink(sharepointUrl: string): string {
    if (!sharepointUrl || !sharepointUrl.includes('sharepoint.com')) {
      return '';
    }

    try {
      // Extraire le domaine (ex: votreentreprise)
      const url = new URL(sharepointUrl);
      const domain = url.hostname.replace('.sharepoint.com', '');

      // Construire le chemin UNC
      const path = url.pathname
        .split('/')
        .filter(part => part) // retire les vides
        .map(decodeURIComponent) // décode les %20 etc.
        .join('\\');

      const unc = `\\\\${domain}.sharepoint.com@SSL\\DavWWWRoot\\${path}`;

      // Encodage pour URL HTML : file:///
      const fileUrl = 'file:///' + unc.replace(/\\/g, '/');

      return fileUrl;
    } catch (e) {
      console.error('Lien SharePoint invalide :', sharepointUrl);
      return '';
    }
  }

  isValidIBAN(iban: string): boolean {
    iban = iban.replace(/\s+/g, '').toUpperCase();
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/;
    if (!ibanRegex.test(iban)) return false;

    // Déplacement des 4 premiers caractères à la fin
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    // Conversion en nombre
    const converted = rearranged.replace(/[A-Z]/g, char =>
      (char.charCodeAt(0) - 55).toString()
    );

    // Modulo 97
    let remainder = converted;
    while (remainder.length > 2) {
      const part = remainder.substring(0, 9);
      const num = parseInt(part, 10);
      const mod = num % 97;
      remainder = mod.toString() + remainder.substring(part.length);
    }

    return parseInt(remainder, 10) % 97 === 1;
  }

  slugify(text: string): string {
    return text
      .toString()
      .normalize('NFD') // Supprime les accents
      .replace(/[\u0300-\u036f]/g, '') // Supprime les caractères diacritiques
      .toLowerCase() // Met en minuscules
      .trim() // Supprime les espaces en début/fin
      .replace(/[^a-z0-9 -]/g, '') // Supprime les caractères spéciaux
      .replace(/\s+/g, '-') // Remplace les espaces par des tirets
      .replace(/-+/g, '-') // Supprime les tirets en double
      .trim();
  }

  slugifyAfs(text: string): string {
    return text
      .toString()
      .normalize('NFD') // Supprime les accents
      .replace(/[\u0300-\u036f]/g, '') // Supprime les caractères diacritiques
      .toLowerCase() // Met en minuscules
      .trim() // Supprime les espaces en début/fin
      .replace(/[^a-z0-9 -]/g, '') // Supprime les caractères spéciaux
      .replace(/\s+/g, '_') // Remplace les espaces par des tirets
      .replace(/-+/g, '_') // Supprime les tirets en double
      .trim();
  }

  initiales(texte: string): string {
    if (!texte) return '';

    const parts = texte
      .trim()
      .split(/[\s\-_]+/) // espace, - ou _
      .filter(Boolean);

    const initials = parts
      .map(part => {
        // find the first alphanumeric/letter character
        const m = part.match(/[A-Za-zÀ-ÖØ-öø-ÿ0-9]+/);
        return m && m[0] ? m[0][0].toLowerCase() : '';
      })
      .filter(Boolean);

    return initials.join('');
  }

  get_cde_report(slug_deal: string) {
    if (!slug_deal || slug_deal == '') return;

    this.popupAndNotifService.showSpinner();
    this.activityservice.track_activity(
      'Download CDE Report',
      slug_deal,
      'deal',
      'cde',
      ''
    );
    this.dealservice.get_cde_report(slug_deal).subscribe({
      next: (res: any) => {
        this.popupAndNotifService.hideSpinner();
        if (res && res.response) {
          this.showPDF(res, false, 'CDE Report');
        } else {
          this.popupAndNotifService.showNotification(
            'CDE report',
            'No report available for this deal.'
          );
        }
      },
      error: (error: any) => {
        this.popupAndNotifService.hideSpinner();
        console.error('Error fetching CDE report:', error);
      },
    });
  }

  get_all_users() {
    return this.http.get<any>(this.serverUrl + 'local/get_all_users').pipe(
      tap(res => {
        this.allUsers = res;
      }),
      catchError(this.handleError)
    );
  }

  public get_all_users_closed_deal(): Observable<any> {
    return this.http
      .get<any>(this.serverUrl + 'local/get_all_users_closed_deal')
      .pipe(
        tap(res => {
          this.allUsers = res;
        }),
        catchError(this.handleError)
      );
  }

  get_all_corporates() {
    return this.http.get<any>(this.serverUrl + 'company/get_corporate').pipe(
      tap(res => {
        this.allCorporates = res;
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('An error occurred:', error);
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }

  redirectToDashboard() {
    const currentUser = this.authService.currentUser();

    if (!currentUser) return;

    const redirect = localStorage.getItem('redirect');
    if (redirect) {
      this.router.navigate([redirect]);
      localStorage.removeItem('redirect');
      return;
    }

    let route = '/crm/crm-dashboard';

    if (
      currentUser.group.includes('admin') ||
      currentUser.group.includes('superadmin')
    ) {
      route = '/admin/admin-dashboard';
    } else if (
      currentUser.group.includes('exec') ||
      currentUser.group.includes('m_exec')
    ) {
      route = '/execution/execution-dashboard';
    } else if (
      currentUser.group.includes('kyc_bo') ||
      currentUser.group.includes('ext_kyc_bo') ||
      currentUser.group.includes('kyc_validator')
    ) {
      route = '/kyc/kyc-dashboard';
    } else if (currentUser.group.includes('billing')) {
      route = '/billing/billing-dashboard';
    } else if (
      currentUser.group.includes('busdev') ||
      currentUser.group.includes('collab') ||
      currentUser.group.includes('m_com')
    ) {
      route = '/crm/crm-dashboard';
    } else if (currentUser.group.includes('payment_agent')) {
      route = '/payment/payment-dashboard';
    } else if (
      currentUser.group.includes('cde_manager') ||
      currentUser.group.includes('cde_admin')
    ) {
      route = '/crm/cde-manager';
    }

    this.router.navigate([route]);
  }

  /**
   * Legacy entry point, kept for its callers: delegates to PopupAndNotifService.confirm().
   * Resolves true only when the user confirmed.
   */
  confirmDialog(
    title: string,
    message: string,
    icon: any = 'info-triangle'
  ): Promise<boolean> {
    return this.popupAndNotifService.confirm(title, message, { tone: legacyConfirmIconTone(icon) });
  }
}
